import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, injectIntl, intlShape, defineMessages } from 'react-intl';
import { withRouteData, withSiteData } from 'react-static';

import Hyperlink from '../common/Hyperlink';
import { type } from '../../styles/typography';
import { getUrlWithStickyParams } from '../../util';
import { validateEmail } from '../../validateInput';
import playIcon from '../../assets/images/home/icon-arrow.svg';

const messages = defineMessages({
  placeholder: {
    id: 'brand.email-form.placeholder',
    defaultMessage: 'Your email address',
  },
  submit: {
    id: 'header.cta.create-free-account',
    defaultMessage: 'Create Free Account',
  },
});

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 595px;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 4px 4px 4px 22px;
  border-radius: var(--b-radius);
  background-color: var(--b-fog);

  input {
    ${type('label', 'large')};
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    padding: 16px 10px 16px 0;
    border: 0;
    background: transparent;
    color: var(--b-black);
    outline: none;

    &::placeholder {
      color: rgba(65, 65, 65, 0.6);
    }
  }

  button {
    ${type('label', 'large')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    flex: 0 0 auto;
    padding: 16px 25px 16px 22px;
    border-radius: 2px;
    background-color: var(--b-black);
    color: var(--b-fog);
    white-space: nowrap;
    transition: background-color 0.16s ease;

    &:hover {
      background-color: var(--b-graphite);
    }

    img {
      width: 14px;
      height: 18px;
    }

    @media (max-width: 479px) {
      padding: 16px;

      span {
        display: none;
      }
    }
  }
`;

const SmallPrint = styled.p`
  ${type('detail', 'xsmall')};
  max-width: 595px;
  margin: 0;
  text-align: left;
  color: ${({ dark }) => (dark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(255, 255, 255, 0.72)')};

  a {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const ErrorText = styled.p`
  ${type('detail', 'xsmall')};
  max-width: 595px;
  margin: 0;
  text-align: left;
  color: var(--c-red);
`;

const UTM_PARAMS = ['utm_medium', 'utm_source', 'utm_campaign'];

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return UTM_PARAMS.reduce((utm, key) => {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
    return utm;
  }, {});
}

function sendEmailSignup(payload) {
  try {
    const body = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon('/api/email-signup', body);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to send email signup', e);
  }
}

class EmailSignupForm extends React.Component {
  state = { email: '', emailError: false };

  onEmailChange = e => {
    const email = e.target.value || '';
    this.setState({ email });
    if (this.state.emailError && validateEmail(email)) {
      this.setState({ emailError: false });
    }
  };

  onEmailBlur = () => {
    const email = (this.emailNode && this.emailNode.value) || '';
    if (email && !validateEmail(email)) {
      this.setState({ emailError: true });
    } else {
      this.setState({ emailError: false });
    }
  };

  onSubmit = e => {
    e.preventDefault();
    const emailValue = (this.emailNode && this.emailNode.value) || '';
    if (emailValue && !validateEmail(emailValue)) {
      this.setState({ emailError: true });
      return;
    }

    const { appDomain, basename, from, language } = this.props;
    const email = emailValue.trim();

    if (typeof window !== 'undefined' && window.analytics) {
      analytics.track('Clicked CTA Button', { title: 'Create Free Account', from, email });
    }

    if (email) {
      sendEmailSignup({ email, from, language, ...getUtmParams() });
    }

    const target = new URL(getUrlWithStickyParams(`${appDomain}/discover`, basename));
    target.searchParams.set('showPreviewsModal', '1');
    if (email) {
      target.searchParams.set('email', email);
    }
    window.location.assign(target.toString());
  };

  render() {
    const { intl, onDark, className, from } = this.props;
    const errorId = `email-signup-error-${from}`;
    return (
      <Form className={className} onSubmit={this.onSubmit} noValidate>
        <Field>
          <input
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder={intl.formatMessage(messages.placeholder)}
            aria-label={intl.formatMessage(messages.placeholder)}
            aria-invalid={this.state.emailError}
            aria-describedby={this.state.emailError ? errorId : undefined}
            value={this.state.email}
            onChange={this.onEmailChange}
            onBlur={this.onEmailBlur}
            ref={node => {
              this.emailNode = node;
            }}
          />
          <button type="submit">
            <img src={playIcon} alt="" />
            <span>{intl.formatMessage(messages.submit)}</span>
          </button>
        </Field>
        {this.state.emailError && (
          <ErrorText id={errorId} role="alert">
            <FormattedMessage
              id="voucher.email-error"
              defaultMessage="Please enter a valid email address"
            />
          </ErrorText>
        )}
        <SmallPrint dark={!onDark}>
          <FormattedMessage
            id="brand.email-form.signup-consent"
            defaultMessage="By creating an account, you accept our {terms}. We may remind you to complete your sign-up and send occasional offers. Unsubscribe for free via the email link. See our {privacyPolicy}."
            values={{
              terms: (
                <Hyperlink to="/terms" openInNewTab>
                  <FormattedMessage id="email-signup-form.terms" defaultMessage="Terms" />
                </Hyperlink>
              ),
              privacyPolicy: (
                <Hyperlink to="/privacy" openInNewTab>
                  <FormattedMessage
                    id="front-page.footer.privacy"
                    defaultMessage="Privacy Policy"
                  />
                </Hyperlink>
              ),
            }}
          />
        </SmallPrint>
      </Form>
    );
  }
}

EmailSignupForm.propTypes = {
  appDomain: PropTypes.string.isRequired,
  basename: PropTypes.string,
  from: PropTypes.string.isRequired,
  language: PropTypes.string,
  onDark: PropTypes.bool,
  className: PropTypes.string,
  intl: intlShape.isRequired,
};

EmailSignupForm.defaultProps = {
  onDark: true,
};

export default withSiteData(withRouteData(injectIntl(EmailSignupForm)));



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/brand/EmailSignupForm.js